(() => {
  const ROOT_SELECTOR = '[data-af-shop-car-care]';

  class AFShopCarCare {
    constructor(root) {
      this.root = root;
      this.gridMount = root.querySelector('[data-af-shop-grid-mount]');
      this.activeTitle = root.querySelector('[data-af-shop-active-title]');
      this.stageRegion = root.querySelector('[data-af-shop-stage-region]');
      this.secondaryMount = root.querySelector('[data-af-shop-secondary-mount]');
      this.sectionId = root.dataset.sectionId;
      this.showSubcategories = root.dataset.showSubcategories !== 'false';
      this.controller = null;

      if (!this.gridMount || !this.stageRegion || !this.secondaryMount) return;

      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      root.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);

      this.syncStageAriaCurrent();
      this.syncSecondaryAriaCurrent();

      const initialStateChanged = this.loadInitialURLState();
      if (!initialStateChanged) this.queueRevealStage(this.getActiveStage(), 'auto');
    }

    getStages() {
      return Array.from(this.root.querySelectorAll('[data-af-shop-stage]'));
    }

    getStageByHandle(handle) {
      if (!handle) return null;
      return this.getStages().find((stage) => stage.dataset.stageHandle === handle) || null;
    }

    getActiveStage() {
      return this.root.querySelector('[data-af-shop-stage].is-active');
    }

    getSecondaryTemplates() {
      return Array.from(this.root.querySelectorAll('[data-af-shop-secondary-template]'));
    }

    getSecondaryTemplate(stage) {
      if (!stage) return null;
      return this.getSecondaryTemplates().find((template) => template.dataset.stageHandle === stage.dataset.stageHandle) || null;
    }

    getSecondaryLinks(scope = this.secondaryMount) {
      return Array.from(scope.querySelectorAll('[data-af-shop-secondary]'));
    }

    getActiveSecondary() {
      return this.secondaryMount.querySelector('[data-af-shop-secondary].is-active');
    }

    getCategoryDefinition(stage, handle) {
      if (!stage || !handle || handle === 'all') {
        return {
          handle: 'all',
          collectionURL: stage ? stage.dataset.collectionUrl : ''
        };
      }

      const template = this.getSecondaryTemplate(stage);
      if (!template) return null;

      const link = Array.from(template.content.querySelectorAll('[data-af-shop-secondary]'))
        .find((candidate) => candidate.dataset.categoryHandle === handle);

      if (!link) return null;
      return {
        handle: link.dataset.categoryHandle,
        collectionURL: link.dataset.collectionUrl
      };
    }

    syncStageAriaCurrent() {
      this.getStages().forEach((stage) => {
        const link = stage.querySelector('.af-media-card');
        if (!link) return;
        if (stage.classList.contains('is-active')) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
    }

    syncSecondaryAriaCurrent() {
      this.getSecondaryLinks().forEach((link) => {
        if (link.classList.contains('is-active')) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
    }

    revealStage(stage, behavior = 'smooth') {
      if (!stage || !window.matchMedia('(max-width: 989px)').matches) return;
      const rail = stage.closest('.af-shop-car-care__stage-rail');
      if (!rail) return;

      const stageRect = stage.getBoundingClientRect();
      const railRect = rail.getBoundingClientRect();
      const currentLeft = rail.scrollLeft;
      const relativeLeft = stageRect.left - railRect.left;
      const targetLeft = currentLeft + relativeLeft - ((rail.clientWidth - stageRect.width) / 2);
      const maxLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const left = Math.max(0, Math.min(targetLeft, maxLeft));
      rail.scrollTo({ left, behavior });
    }

    queueRevealStage(stage, behavior = 'smooth') {
      if (!stage) return;
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => this.revealStage(stage, behavior));
      });
    }

    revealSecondary(link, behavior = 'smooth') {
      if (!link) return;
      const rail = link.closest('.af-shop-car-care__secondary-rail');
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      link.scrollIntoView({ behavior, block: 'nearest', inline: 'nearest' });
    }

    setActiveStage(stage) {
      if (!stage) return;
      this.getStages().forEach((candidate) => {
        const isActive = candidate === stage;
        candidate.classList.toggle('is-active', isActive);
        if (isActive) candidate.dataset.active = 'true';
        else candidate.removeAttribute('data-active');
      });

      this.syncStageAriaCurrent();
      if (this.activeTitle && stage.dataset.stageTitle) this.activeTitle.textContent = stage.dataset.stageTitle;
      this.queueRevealStage(stage);
    }

    renderSecondaryNav(stage, activeHandle = 'all') {
      if (!this.showSubcategories) {
        this.secondaryMount.innerHTML = '';
        return;
      }

      const template = this.getSecondaryTemplate(stage);
      this.secondaryMount.innerHTML = template ? template.innerHTML : '';

      let activeLink = this.getSecondaryLinks().find((link) => link.dataset.categoryHandle === activeHandle);
      if (!activeLink) activeLink = this.getSecondaryLinks().find((link) => link.dataset.categoryHandle === 'all');

      this.getSecondaryLinks().forEach((link) => link.classList.toggle('is-active', link === activeLink));
      this.syncSecondaryAriaCurrent();

      if (activeLink) {
        window.requestAnimationFrame(() => this.revealSecondary(activeLink, 'auto'));
      }
    }

    buildEndpointURL(resourceURL) {
      const url = new URL(resourceURL, window.location.origin);
      url.searchParams.delete('section_id');
      if (!this.sectionId) throw new Error('Shop Car Care section id is missing.');
      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    buildPageStateURL(stage, resourceURL = null, categoryHandle = 'all') {
      const url = new URL(window.location.href);
      const resource = resourceURL ? new URL(resourceURL, window.location.origin) : null;

      url.searchParams.set('shop', stage.dataset.stageHandle);
      url.searchParams.delete('category');
      if (categoryHandle && categoryHandle !== 'all') url.searchParams.set('category', categoryHandle);
      url.searchParams.delete('page');

      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.startsWith('filter.')) url.searchParams.delete(key);
      });

      if (resource) {
        const page = resource.searchParams.get('page');
        if (page) url.searchParams.set('page', page);
        resource.searchParams.forEach((value, key) => {
          if (key.startsWith('filter.')) url.searchParams.append(key, value);
        });
      }

      return url;
    }

    async replaceGrid(resourceURL, stage, options = {}) {
      const {
        updateHistory = true,
        scrollToGrid = false,
        historyMode = 'push',
        categoryHandle = 'all'
      } = options;

      if (!resourceURL || !stage) return;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.gridMount.setAttribute('aria-busy', 'true');
      this.root.classList.add('is-loading');

      try {
        const response = await fetch(this.buildEndpointURL(resourceURL), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error(`Grid request failed: ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = documentFragment.querySelector('[data-af-collection-grid]');
        if (!nextGrid) throw new Error('AFDS collection grid not found in section response.');

        this.gridMount.innerHTML = nextGrid.outerHTML;
        this.setActiveStage(stage);
        this.renderSecondaryNav(stage, categoryHandle);

        if (updateHistory) {
          const stateURL = this.buildPageStateURL(stage, resourceURL, categoryHandle);
          const method = historyMode === 'replace' ? 'replaceState' : 'pushState';
          window.history[method]({ afShop: stage.dataset.stageHandle, afCategory: categoryHandle }, '', stateURL);
        }

        this.root.dispatchEvent(new CustomEvent('af:shop-car-care:grid-updated', {
          bubbles: true,
          detail: {
            stage: stage.dataset.stageHandle,
            category: categoryHandle,
            collectionURL: resourceURL.toString ? resourceURL.toString() : resourceURL
          }
        }));

        document.dispatchEvent(new CustomEvent('af:collection-grid:ready', {
          detail: { root: this.gridMount.querySelector('[data-af-collection-grid]') }
        }));

        if (scrollToGrid) {
          const top = this.root.querySelector('[data-af-shop-shopping]');
          if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[AF Shop Car Care]', error);
        window.location.assign(resourceURL);
      } finally {
        this.gridMount.setAttribute('aria-busy', 'false');
        this.root.classList.remove('is-loading');
      }
    }

    resourceURLForState(stage, categoryHandle, pageURL) {
      const category = this.getCategoryDefinition(stage, categoryHandle) || this.getCategoryDefinition(stage, 'all');
      if (!category || !category.collectionURL) return null;

      const resourceURL = new URL(category.collectionURL, window.location.origin);
      const page = pageURL.searchParams.get('page');
      if (page) resourceURL.searchParams.set('page', page);
      pageURL.searchParams.forEach((value, key) => {
        if (key.startsWith('filter.')) resourceURL.searchParams.append(key, value);
      });
      return { resourceURL, categoryHandle: category.handle };
    }

    onClick(event) {
      const stageLink = event.target.closest('[data-af-shop-stage] .af-media-card');
      if (stageLink && this.root.contains(stageLink)) {
        const stage = stageLink.closest('[data-af-shop-stage]');
        if (!stage || !stage.dataset.collectionUrl) return;
        event.preventDefault();

        if (stage.classList.contains('is-active')) {
          const shopping = this.root.querySelector('[data-af-shop-shopping]');
          if (shopping) shopping.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        this.replaceGrid(stage.dataset.collectionUrl, stage, {
          updateHistory: true,
          scrollToGrid: true,
          categoryHandle: 'all'
        });
        return;
      }

      const secondaryLink = event.target.closest('[data-af-shop-secondary]');
      if (secondaryLink && this.secondaryMount.contains(secondaryLink)) {
        const stage = this.getActiveStage();
        if (!stage || !secondaryLink.dataset.collectionUrl) return;
        event.preventDefault();

        const categoryHandle = secondaryLink.dataset.categoryHandle || 'all';
        if (secondaryLink.classList.contains('is-active')) return;

        this.replaceGrid(secondaryLink.dataset.collectionUrl, stage, {
          updateHistory: true,
          scrollToGrid: false,
          categoryHandle
        });
        return;
      }

      const paginationLink = event.target.closest('[data-af-shop-grid-mount] [data-af-collection-pagination] a[href]');
      if (paginationLink && this.root.contains(paginationLink)) {
        const stage = this.getActiveStage();
        const categoryHandle = this.getActiveSecondary()?.dataset.categoryHandle || 'all';
        if (!stage) return;

        event.preventDefault();
        this.replaceGrid(paginationLink.href, stage, {
          updateHistory: true,
          scrollToGrid: true,
          categoryHandle
        });
      }
    }

    loadInitialURLState() {
      const url = new URL(window.location.href);
      const requestedStageHandle = url.searchParams.get('shop');
      const requestedCategoryHandle = this.showSubcategories ? (url.searchParams.get('category') || 'all') : 'all';
      const stage = this.getStageByHandle(requestedStageHandle) || this.getActiveStage();
      const active = this.getActiveStage();
      if (!stage || !active) return false;

      const state = this.resourceURLForState(stage, requestedCategoryHandle, url);
      if (!state) return false;

      const stageChanged = stage !== active;
      const categoryChanged = state.categoryHandle !== 'all';
      const paged = url.searchParams.has('page') || Array.from(url.searchParams.keys()).some((key) => key.startsWith('filter.'));
      if (!stageChanged && !categoryChanged && !paged) return false;

      this.replaceGrid(state.resourceURL, stage, {
        updateHistory: false,
        scrollToGrid: false,
        categoryHandle: state.categoryHandle
      });
      return true;
    }

    onPopState() {
      const url = new URL(window.location.href);
      const stage = this.getStageByHandle(url.searchParams.get('shop')) || this.getStages()[0];
      if (!stage) return;

      const requestedCategory = this.showSubcategories ? (url.searchParams.get('category') || 'all') : 'all';
      const state = this.resourceURLForState(stage, requestedCategory, url);
      if (!state) return;

      this.replaceGrid(state.resourceURL, stage, {
        updateHistory: false,
        scrollToGrid: false,
        categoryHandle: state.categoryHandle
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.afShopCarCareInitialised === 'true') return;
      root.dataset.afShopCarCareInitialised = 'true';
      new AFShopCarCare(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
